import React, { useState } from 'react';
import { X, Plus, Minus, ShoppingBag, Trash2, Leaf } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '../../context/CartContext';
import { Link, useNavigate } from 'react-router-dom';

const CartDrawer = () => {
  const [imageErrors, setImageErrors] = useState({});
  const navigate = useNavigate();
  const {
    cartItems,
    isCartOpen,
    closeCart,
    removeFromCart,
    updateQuantity,
    cartTotal,
    clearCart,
    cartItemsCount,
  } = useCart();

  const handleImageError = (id) => {
    setImageErrors(prev => ({ ...prev, [id]: true }));
  };

  if (!isCartOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity duration-300"
        onClick={closeCart}
        onKeyDown={(e) => e.key === 'Escape' && closeCart()}
        role="button"
        tabIndex={0}
        aria-label="Close cart"
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl transform transition-transform duration-300 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-[#2e4e18] text-white flex-shrink-0">
          <div className="flex items-center gap-3">
            <ShoppingBag className="w-6 h-6" />
            <h2 className="text-xl font-bold">Your Cart</h2>
            <span className="bg-[#548B2E] text-white text-sm px-3 py-1 rounded-full">
              {cartItemsCount} {cartItemsCount === 1 ? 'item' : 'items'}
            </span>
          </div>
          <button
            onClick={closeCart}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6 pb-4">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingBag className="w-24 h-24 text-gray-300 mb-6" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Your cart is empty</h3>
              <p className="text-gray-500 mb-6">Start adding fresh produce to your cart!</p>
              <Link
                to="/shop"
                onClick={closeCart}
                className="bg-[#548B2E] text-white px-8 py-3 rounded-full font-medium hover:bg-[#3f6b20] transition-colors"
              >
                Browse Products
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item) => (
                <div key={item.id} className="flex gap-4 rounded-xl p-4 bg-gray-50">
                  {/* Product Image */}
                  <div className="w-20 h-20 bg-[#f2f8eb] rounded-lg flex items-center justify-center flex-shrink-0 relative">
                    {imageErrors[item.id] ? (
                      <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center">
                        <Leaf className="w-6 h-6 text-green-600" />
                      </div>
                    ) : (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-12 h-12 object-contain"
                        onError={() => handleImageError(item.id)}
                      />
                    )}
                  </div>

                  {/* Product Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-800 truncate">{item.name}</h4>
                      {(item.flashSku || item.originalPrice) && (
                        <span className="flex-shrink-0 text-[10px] font-bold text-orange-600 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded">
                          FLASH DEAL
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{item.weight}</p>
                    {item.originalPrice ? (
                      <div className="flex items-center gap-2 mt-1">
                        <p className="font-bold text-orange-600">₹{item.price}</p>
                        <p className="text-xs text-gray-400 line-through">₹{item.originalPrice}</p>
                      </div>
                    ) : (
                      <p className="font-bold text-[#2e4e18] mt-1">₹{item.price}</p>
                    )}
                  </div>

                  {/* Quantity Controls */}
                  <div className="flex flex-col items-end gap-2">
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-2 bg-white rounded-full border border-gray-200">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center font-medium">{item.quantity}</span>
                      <button
                        onClick={() => {
                          if (item.flashSku || item.originalPrice) {
                            const numericProductId = item.productId ?? (typeof item.id === 'number' ? item.id : null);
                            toast.warning('Only 1 Flash Deal unit can be added per item.', {
                              description: 'Navigating to the regular listing.',
                            });
                            if (numericProductId) {
                              closeCart();
                              navigate('/shop', { state: { scrollToProduct: numericProductId } });
                            }
                            return;
                          }
                          updateQuantity(item.id, item.quantity + 1);
                        }}
                        className={`p-2 rounded-full transition-colors ${item.flashSku || item.originalPrice ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="p-6 bg-white border-t border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-600">Subtotal</span>
              <span className="text-2xl font-bold text-[#2e4e18]">₹{cartTotal}</span>
            </div>
            <p className="text-sm text-gray-500 mb-4">Delivery charges calculated at checkout</p>
            <div className="flex gap-3">
              <button
                onClick={clearCart}
                className="px-4 py-3 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-100 transition-colors"
              >
                Clear
              </button>
              <button
                onClick={() => { closeCart(); navigate('/checkout'); }}
                className="flex-1 bg-[#548B2E] text-white py-3 rounded-full font-medium hover:bg-[#3f6b20] transition-colors"
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;